import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { MAX_WHEEL_STEER } from './wheelMotion.js';

// Front cutouts reach the visible lip when allowing for steering. The rear
// clearance stays inside its already rounded authored arch, so it needs fewer
// sides. Keep the extra silhouette detail local to the exposed front opening.
const FRONT_SEGMENTS = 40;
const REAR_SEGMENTS = 12;
const GAP = 0.025;
const EPS = 1e-7;

/** Conservative envelopes around the rolling/steering tires, in model space.
 * The polygon circumscribes the circular clearance (not the other way round).
 */
export function wheelWellVolumes(parts) {
  const tires = parts.filter(p => p.anim === 'wheelF' && p.wheelTire);
  const rearZ = parts.find(p => p.anim === 'wheel').pos[2];
  return tires.flatMap(tire => {
    tire.geo.computeBoundingBox();
    const halfWidth = tire.geo.boundingBox.getSize(new THREE.Vector3()).x / 2;
    return [true, false].map(front => {
      const [x, y] = tire.pos, z = front ? tire.pos[2] : rearZ;
      const r = tire.wheelRadius;
      const turn = front ? MAX_WHEEL_STEER : 0;
      const radius = (front ? Math.hypot(r, halfWidth) : r) + GAP;
      const inner = Math.abs(x) - r * Math.sin(turn) - halfWidth * Math.cos(turn) - GAP;
      const sign = Math.sign(x);
      const segments = front ? FRONT_SEGMENTS : REAR_SEGMENTS;
      const planes = [[-sign, 0, 0, -inner]];
      for (let i = 0; i < segments; i++) {
        const a = i * Math.PI * 2 / segments, ny = Math.cos(a), nz = Math.sin(a);
        planes.push([0, ny, nz, radius + ny * y + nz * z]);
      }
      return { x, y, z, radius, inner, sign, segments, planes, bottom: Math.min(0.24, r * 0.5) };
    });
  });
}

const distance = (v, p) => v[0] * p[0] + v[1] * p[1] + v[2] * p[2] - p[3];

// Split a convex face while interpolating normals. All clipping happens once,
// when building assets; the resulting meshes use the ordinary Fleet materials.
function split(poly, plane) {
  const inside = [], outside = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const da = distance(a, plane), db = distance(b, plane);
    (da <= 0 ? inside : outside).push(a);
    if ((da < 0 && db > 0) || (da > 0 && db < 0)) {
      const t = da / (da - db), cut = a.map((v, j) => v + (b[j] - v) * t);
      inside.push(cut); outside.push(cut);
    }
  }
  return [inside, outside];
}

function subtract(poly, planes) {
  // Reject untouched triangles before partitioning them along the cut planes.
  if (planes.some(p => poly.every(v => distance(v, p) >= -EPS))) return [poly];
  const kept = [];
  let remaining = poly;
  for (const plane of planes) {
    const [inside, outside] = split(remaining, plane);
    if (outside.length >= 3) kept.push(outside);
    remaining = inside;
    if (remaining.length < 3) break;
  }
  return kept;
}

function carve(geo, wells) {
  const g = geo.index ? geo.toNonIndexed() : geo;
  const p = g.attributes.position, n = g.attributes.normal;
  const positions = [], normals = [];
  const a = new THREE.Vector3(), b = new THREE.Vector3();
  for (let i = 0; i < p.count; i += 3) {
    let polys = [Array.from({ length: 3 }, (_, j) => [p.getX(i + j), p.getY(i + j), p.getZ(i + j), n.getX(i + j), n.getY(i + j), n.getZ(i + j)])];
    for (const well of wells) polys = polys.flatMap(poly => subtract(poly, well.planes));
    for (const poly of polys) for (let j = 1; j < poly.length - 1; j++) {
      const triangle = [poly[0], poly[j], poly[j + 1]];
      a.set(...triangle[1].slice(0, 3)).sub(b.set(...triangle[0].slice(0, 3)));
      b.set(...triangle[2].slice(0, 3)).sub(new THREE.Vector3(...triangle[0].slice(0, 3)));
      if (a.cross(b).lengthSq() < 1e-16) continue;
      for (const v of triangle) {
        positions.push(v[0], v[1], v[2]);
        const length = Math.hypot(v[3], v[4], v[5]) || 1;
        normals.push(v[3] / length, v[4] / length, v[5] / length);
      }
    }
  }
  const result = new THREE.BufferGeometry();
  result.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  result.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  return result;
}

/** Open-bottom wheelhouse: inward-facing roof/sides and a recessed back wall. */
function lining(w, shell) {
  const ring = [];
  // Use the same circumscribed polygon as the cut so the two boundaries agree.
  const radius = w.radius / Math.cos(Math.PI / w.segments);
  for (let i = 0; i < w.segments; i++) {
    const a = (i + 0.5) * Math.PI * 2 / w.segments;
    ring.push([w.sign * w.inner, w.y + radius * Math.cos(a), w.z + radius * Math.sin(a)]);
  }
  const [upper] = split(ring, [0, -1, 0, -w.bottom]);
  // Close the tunnel all the way to the actual body/fender surface. A constant
  // inset leaves a slit behind the outer arch when viewed diagonally on a turn.
  const ray = new THREE.Raycaster();
  const mouthAt = (point) => {
    ray.set(new THREE.Vector3(w.sign * 4, point[1], point[2]), new THREE.Vector3(-w.sign, 0, 0));
    const hit = ray.intersectObject(shell, true).find(h => w.sign * h.point.x >= w.inner - EPS);
    return hit ? Math.max(w.inner, Math.abs(hit.point.x)) : w.inner;
  };
  const positions = [], normals = [];
  const tri = (a, b, c, normal, smooth = false) => {
    const cross = new THREE.Vector3().subVectors(new THREE.Vector3(...b), new THREE.Vector3(...a))
      .cross(new THREE.Vector3().subVectors(new THREE.Vector3(...c), new THREE.Vector3(...a)));
    if (cross.lengthSq() < 1e-16) return;
    if (cross.dot(normal) < 0) [b, c] = [c, b];
    positions.push(...a, ...b, ...c);
    for (const v of [a, b, c]) {
      // Smooth only the curved tunnel; keep the recessed back wall flat.
      const n = smooth ? new THREE.Vector3(0, w.y - v[1], w.z - v[2]).normalize() : normal;
      normals.push(n.x, n.y, n.z);
    }
  };
  const facing = new THREE.Vector3(w.sign, 0, 0);
  for (let i = 1; i < upper.length - 1; i++) tri(upper[0], upper[i], upper[i + 1], facing);
  for (let i = 0; i < upper.length; i++) {
    const a = upper[i], b = upper[(i + 1) % upper.length];
    if (Math.abs(a[1] - w.bottom) < EPS && Math.abs(b[1] - w.bottom) < EPS) continue;
    const mid = [0, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
    const mouth = Math.max(mouthAt(a), mouthAt(b), mouthAt(mid));
    const c = [w.sign * mouth, b[1], b[2]], d = [w.sign * mouth, a[1], a[2]];
    const inward = new THREE.Vector3(0, w.y - (a[1] + b[1]) / 2, w.z - (a[2] + b[2]) / 2);
    tri(a, b, c, inward, true); tri(a, c, d, inward, true);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  return geo;
}

export function finishWheelWells(parts, liningMaterial) {
  const wells = wheelWellVolumes(parts);
  const shell = new THREE.Group();
  for (const p of parts) if (!p.anim && !p.wheelHardware) shell.add(new THREE.Mesh(p.geo, p.mat));
  shell.updateMatrixWorld(true);
  const result = [];
  for (const part of parts) {
    if (part.anim || part.wheelHardware) { result.push(part); continue; }
    // Static car descriptors are authored in model space (zero local transform).
    const geo = carve(part.geo, wells);
    if (geo.attributes.position.count) result.push({ ...part, geo });
  }
  // Reuse the existing cladding bucket: no new materials or draw calls.
  const target = result.find(p => !p.anim && !p.wheelHardware && p.mat === liningMaterial);
  target.geo = mergeGeometries([target.geo, ...wells.map(w => lining(w, shell))]);
  return result;
}
